import {
  resolveCanonicalTreeRelationshipCreationPolicy,
} from '@/governor/tree/CanonicalTreeRelationshipCreationPolicy';

import {
  selectCanonicalTreeGenerationAnchorCandidates,
} from '@/governor/tree/CanonicalTreeTopologyAuthority';
import { useTreeGovernorEvent } from '@/hooks/governor/useTreeGovernorEvent';
import { emitTreeGovernorEvent } from '@/lib/graph/graphMutationAudit';


import { cleanParentIds } from '@/lib/graphIdSafety';
import { emitModernFamilyRuntimeProof } from '@/lib/modernFamilyRuntimeProof';
import { validateModernFamilyRelationship } from '@/lib/modernFamilyGovernorValidator';
import { emitGovernorLiveAction } from '@/lib/governorEventBus';
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { DEV_BYPASS_TEST_MODE } from '@/lib/devBypass';
import { MODERN_FAMILY_RELATIONSHIP_REGISTRY, getModernFamilyRelationshipSpec } from '@/lib/modernFamilyRelationshipRegistry';

import {
  RELATIONSHIP_OPTIONS,
  EXTENDED_RELATIONSHIP_OPTIONS,
  findExtendedOption,
  buildRelationshipWorkflow,
  Side,
  Relationship,
  LivingStatus,
  useFamilyGraph,
  GraphMember,
  type TreeRelationshipWorkflow,
} from '@/contexts/FamilyGraphContext';
import { useAuth } from '@/contexts/AuthContext';

import {
  Loader2,
  UserPlus,
  AlertTriangle,
  Link2,
  HelpCircle,
  Check,
} from 'lucide-react';
import MemberAvatar from './MemberAvatar';
import PhotoUpload from './PhotoUpload';
import BirthdayInput, {
  emptyBirthday,
  birthdayToPayload,
  type BirthdayValue,
} from './BirthdayInput';
import {
  checkAddSafeguards,
  relationshipGuidance,
  debugAuditFamilyGraph,
} from '@/lib/treeSafeguards';
import {
  addRelativeUniversal,
  connectExistingUniversal,
  type AddRelativeResult,
} from '@/lib/addRelativeUniversal';
import {
  findDuplicateMatches,
  recordDuplicateEvent,
  type DuplicateMatch,
} from '@/lib/duplicates/duplicateGuard';
import {
  runDuplicatePreflight,
} from '@/lib/duplicates/globalDuplicateGuard';
import { isDuplicateGuardrailEnabled } from '@/lib/ai/featureFlag';
import DuplicateConfirmModal from '@/components/duplicates/DuplicateConfirmModal';




interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSide?: Side;
  defaultRelationship?: Relationship;
  // Prefill name (used by smart suggestions)
  defaultName?: string;
  /**
   * The id of the node the user tapped "+" from. When set, this becomes
   * the pre-selected parent for relationship='child' (and the suggested
   * connector for any other relationship type whose strict structural
   * rules accept it). The tapped node ALWAYS becomes parent_id for the
   * 'child' branch — there is no longer a viewer+spouse restriction.
   * Edge case: when undefined (e.g. global "Add to Mom's side"), the
   * modal falls back to requiring an explicit parent pick for 'child'.
   */
  defaultParentId?: string | null;
  /**
   * Extended relationship key OR free-form custom label that was selected
   * upstream (e.g. by the quick-add menu's "Grandchild" / "Great-Aunt /
   * Uncle" / "Niece / Nephew" / "Godmother" buttons). When set, the
   * modal preselects the matching `EXTENDED_RELATIONSHIP_OPTIONS` entry
   * in its dropdown so the advanced label is preserved verbatim — no
   * more "I picked Grandchild but the modal shows Child" mismatch.
   *
   * If the value matches a known extended option (by `value`,
   * `displayOverride`, or `label`), that virtual entry is preselected
   * and the corresponding `displayOverride` / core `Relationship` /
   * default `Side` are used. Otherwise the value is treated as a
   * free-form custom label and written to `relationship_override` on
   * save while the structural relationship falls back to
   * `defaultRelationship` (or 'unknown').
   */
  defaultRelationshipOverride?: string | null;
  defaultRelationshipWorkflow?:
    TreeRelationshipWorkflow | null;
  // Callback after successful add (used to chain suggestions)
  onAdded?: (member: GraphMember) => void;
}



const AddGraphMemberModal: React.FC<Props> = ({
  open,
  onOpenChange,
  defaultSide = 'mother',
  defaultRelationship,
  defaultName,
  defaultParentId,
  defaultRelationshipOverride,
  defaultRelationshipWorkflow,
  onAdded,
}) => {

  const {
    addMember,
    updateMember,
    findPossibleDuplicates,
    members,
    connectExistingAncestryAtomic,
    linkParents,
    linkSpouses,
    rollbackCreatedMember,
    viewerMemberId,
    effectiveOwnerUserId,
    claimProfile,
    refresh,
    runBulkAdd,
    scheduleRefresh,
  } = useFamilyGraph();

  // ---- AUTH (used for photo uploads + auto-approve gating) ----
  // BUG FIX: previously `useAuth` was imported but never invoked, so
  // references below to `user?.id` (PhotoUpload userId prop) and
  // `isStaff` (auto-approve gate, submit button label, pending banner)
  // threw ReferenceError the moment this modal mounted, taking the
  // whole app down. Restore the destructure with safe fallbacks so an
  // unauthenticated session (or a context boot race) renders cleanly
  // instead of crashing.
  const auth = useAuth();
  const user = auth?.user ?? null;
  const isStaff = !!auth?.isStaff;
  const isOwner = !!auth?.isOwner;
  const role = auth?.role ?? 'member';

  // ---- DEV OWNER/ADMIN GATE (deploypad.app only) ----
  // Approval gating is intentionally widened so the dev project can
  // exercise the modern relationship engine end-to-end without an
  // approval workflow.
  //
  // `canAddWithoutApproval` is the SINGLE source of truth for:
  //   • the submit button label  ("Save member" vs "Submit for approval")
  //   • the approval_status written on the new row ('approved' vs 'pending')
  //   • the "pending" banner under the form (hidden when true)
  //
  // It returns true when ANY of:
  //   1. the real auth context flags us as staff (owner / admin) — same
  //      behaviour production has always had;
  //   2. DEV_BYPASS_TEST_MODE is active (only on *.deploypad.app — the
  //      dev role bypass in AuthContext also flips isStaff true through
  //      the role override, but we double-up here as a belt-and-braces
  //      failsafe so a context-render race can never accidentally
  //      surface the "Submit for approval" path on the dev project);
  //   3. the signed-in user is the only person in the loaded graph
  //      (fresh tree → they ARE the de-facto owner; no approval needed
  //      for the very first relative they add). This is the spec's
  //      "if the signed-in user … is the only user in the dev family
  //      graph: set canAddWithoutApproval = true" rule.
  const visibleMembers = members.filter((m) => {
  if (!effectiveOwnerUserId) return true;
  return m.ownerUserId === effectiveOwnerUserId;
});

const isSoleGraphOwner = useMemo(() => {
    if (!user) return false;
    const live = visibleMembers.filter((m) => m.approvalStatus !== 'rejected');

    // Fresh graph that ONLY contains the auto-created YOU placeholder
    // (or nothing yet). A second real relative would push count > 1.
    if (live.length === 0) return true;
    if (live.length === 1) {
      const only = live[0];
      if (only.side === 'self') return true;
    }
    return false;
  }, [members, user]);

  // ─── INVITED-USER ADD-RELATIVE GATE (production fix) ─────────────────
  // True when this user joined someone else's tree (invited paid member).
  // Used by the viewer-resolution logic below to refuse the original
  // loose `side='self'` fallback in shared graphs — that fallback would
  // otherwise match the INVITER's own anchor row and silently attach
  // the new relative to the INVITER's parent_ids instead of the
  // logged-in user's. The invited user's anchor in a shared graph is
  // ALWAYS their claimed placeholder (claimedByUserId === user.id),
  // never a side='self' row owned by someone else.
  const isInSharedGraph =
    !!user?.id && !!effectiveOwnerUserId && effectiveOwnerUserId !== user.id;

  // ─── ISSUE 3 FIX — Remove "admin / 2-member confirm" gate for paid users ──
  //
  // Root cause: the old gate `canAddWithoutApproval = isStaff ||
  // DEV_BYPASS_TEST_MODE || isSoleGraphOwner` evaluated to FALSE for any
  // invited paid user who joined someone else's tree. Their adds were
  // written with approval_status='pending', the amber "until the family
  // owner, an admin, or 2 family members confirm them" banner showed
  // BEFORE the form, the submit button was labeled "Submit for approval",
  // and the success toast said "submitted — pending approval". For
  // normal family-building this was wrong: a paid authenticated user
  // adding her own mother/father/sibling/child/spouse from her centered
  // "You" view does NOT need owner/admin approval.
  //
  // Fix: any authenticated user with a valid viewer anchor (either
  // `viewerMemberId` from context, a claimed self-row, or a side='self'
  // placeholder they own) is allowed to add a relative without approval
  // gating. This is the spec's "paid users must be able to add close
  // relatives to their own tree view without admin approval blocking
  // the action" rule applied universally. Duplicate-prevention guard
  // (`duplicateOverride === 'unsure'`) continues to write 'pending' for
  // legitimately ambiguous additions — that path is preserved.
  //
  // NEVER touches: payments, subscriptions, access gating, auth,
  // pricing, uploads, invites, RLS, or relationship assignment.
  const hasViewerAnchor =
    !!viewerMemberId ||
    !!members.find(
      (m) =>
        !!user?.id &&
        (m.claimedByUserId === user.id ||
          (m.ownerUserId === user.id &&
            m.side === 'self' &&
            (m.relationship === 'unknown' ||
              (m.relationship as string) === 'self'))) &&
        m.approvalStatus !== 'rejected',
    );

  const canAddWithoutApproval =
    isStaff ||
    DEV_BYPASS_TEST_MODE ||
    isSoleGraphOwner ||
    // Any authenticated user adding a relative to their OWN anchored
    // tree view skips the approval gate. This is the spec's "every
    // paid authenticated user must be able to add their own close
    // relatives" rule. The viewer-anchor check guarantees we never
    // grant approval autonomy to an unauthenticated visitor or to a
    // user whose self-row hasn't been bound yet (those flows still
    // get the safety-net `ensureViewerAnchor()` in `insertNew`).
    (!!user?.id && hasViewerAnchor);



  // Permanent diagnostics — log the role-resolution snapshot once per
  // open, so a future "owner shows pending" report is traceable in
  // seconds. Never logs PII beyond the auth uid, which the user can
  // already read from their own session.
  React.useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line no-console
    console.log('[KinsCircle] AddGraphMemberModal role-resolution', {
      authUid: user?.id ?? null,
      viewerMemberId: viewerMemberId ?? null,
      role,
      isOwner,
      isStaff,
      DEV_BYPASS_TEST_MODE,
      isSoleGraphOwner,
      canAddWithoutApproval,
      memberCount: members.length,
    });
  }, [open, user?.id, viewerMemberId, role, isOwner, isStaff, isSoleGraphOwner, canAddWithoutApproval, members.length]);

  // (The literal `approval_status` value that will be written on the
  // next save attempt is computed lower in the component, AFTER the
  // `duplicateOverride` state is declared — see `approvalStatusToWrite`
  // further down. We declare it later because referencing
  // `duplicateOverride` here would hit the const temporal dead zone.)


  // ---- VIEWER RESOLUTION ("YOU" anchor for parent/step-parent links) ----
  //
  // CRITICAL PRODUCTION FIX (Dec 2026) — INVITED PAID USER ADD-RELATIVE
  //
  // The previous fallback matched ANY `side === 'self'` row with a
  // placeholder relationship, REGARDLESS OF WHO OWNED IT. In a shared
  // graph (invited paid user who joined someone else's tree) this
  // silently picked up the ORIGINAL TREE OWNER'S anchor row as the
  // invited user's "YOU" — so when the invited user clicked
  // "Add to Mom's side" + relationship='mother' and saved, the new
  // mother was appended to the INVITER's parent_ids instead of the
  // invited user's. To the invited user it looked like "the app is
  // not allowing it" because:
  //   (a) RLS denied the linkParents write against the inviter's row, OR
  //   (b) the write succeeded but the new mother appeared on the
  //       inviter's branch, never on the invited user's centered tree.
  //
  // The new resolution ladder is USER-SCOPED at every step:
  //   1. `viewerMemberId` from context  — the strictly-canonical answer.
  //   2. A row CLAIMED by the logged-in user (`claimedByUserId === user.id`).
  //      Covers the brief bootstrap race where the membership row is
  //      saved but `viewerMemberId` hasn't propagated yet.
  //   3. A `side='self'` placeholder OWNED by the logged-in user. The
  //      ownership scope guarantees we never pick someone else's anchor.
  //
  // When the ladder returns null AND a viewer-anchored relationship is
  // being saved (mother / father / step / adopted / sibling / child via
  // viewer-fallback), `insertNew` auto-creates a self-anchor row owned
  // by the logged-in user via `claimProfile` so the add never fails for
  // an invited user whose placeholder hasn't been auto-bound yet.
  const effectiveViewerId = useMemo<string | null>(() => {
    if (viewerMemberId) return viewerMemberId;
    if (!user?.id) return null;
    // 2. Claim-scoped lookup — always wins over any side='self' row.
    const claimedByMe = members.find(
      (m) =>
        m.claimedByUserId === user.id &&
        m.approvalStatus !== 'rejected',
    );
    if (claimedByMe) return claimedByMe.id;
    // 3. USER-SCOPED self-anchor placeholder ONLY. No anonymous match.
    const myPlaceholder = members.find(
      (m) =>
        m.side === 'self' &&
        m.ownerUserId === user.id &&
        (m.relationship === 'unknown' || (m.relationship as string) === 'self') &&
        m.approvalStatus !== 'rejected',
    );
    return myPlaceholder?.id ?? null;
  }, [viewerMemberId, members, user?.id]);






  // ---- EXTENDED-RELATIONSHIP STATE ----
  // The dropdown's selected key. Can be either a core Relationship enum
  // value (e.g. 'mother', 'cousin') OR a virtual extended key (e.g.
  // 'v_grandchild', 'v_great_aunt_uncle'). The structural `relationship`
  // (used for parent_ids / connector / placement) is derived from this
  // key via EXTENDED_RELATIONSHIP_OPTIONS.coreRelationship; the displayed
  // label override is derived from EXTENDED_RELATIONSHIP_OPTIONS.displayOverride.
  // This is the SHARED source of truth between the quick-add menu and
  // this dropdown — whatever advanced label the user picked upstream
  // (Grandchild / Great-Aunt / Niece-Nephew / 2nd Cousin / Step-Mom /
  // In-laws / Family Friend / etc.) is preserved verbatim here.
  const initialExtendedKey = (): string => {
    // 1. Direct override match → preselect that virtual entry.
    const fromOverride = findExtendedOption(defaultRelationshipOverride);
    if (fromOverride) return fromOverride.value;
    // 2. Core enum match → use the core entry directly.
    if (defaultRelationship) {
      const core = EXTENDED_RELATIONSHIP_OPTIONS.find(
        (o) => o.value === defaultRelationship,
      );
      if (core) return core.value;
    }
    // 3. Default fallback.
    return 'mother';
  };
  const [selectedExtendedKey, setSelectedExtendedKey] = useState<string>(
    initialExtendedKey(),
  );

  /*
   * Card-originated canonical workflow evidence.
   *
   * Preserved only while the user keeps the relationship initially
   * selected by the card action. Any manual relationship change clears
   * this state so an endpoint can never leak into a different workflow.
   */
  const [
    preservedRelationshipWorkflow,
    setPreservedRelationshipWorkflow,
  ] = useState<TreeRelationshipWorkflow | null>(null);

  const creatableRelationshipOptions = useMemo(
    () =>
      EXTENDED_RELATIONSHIP_OPTIONS.filter(
        option =>
          resolveCanonicalTreeRelationshipCreationPolicy(
            option.value,
          ).disposition === 'CREATE',
      ),
    [],
  );
  // Free-form custom display label — visible only when the selected
  // extended option has a `displayOverride` OR the upstream caller passed
  // a `defaultRelationshipOverride` that didn't match any virtual entry
  // (truly free-form, e.g. "Godmother", "Step-cousin once removed"). The
  // user can edit it before saving. Written to `relationship_override`.
  const [customLabel, setCustomLabel] = useState<string>('');

  const { emit } = useTreeGovernorEvent('add-graph-member-modal');
  const [name, setName] = useState(defaultName ?? '');
  const [relationship, setRelationship] = useState<Relationship>(defaultRelationship ?? 'mother');
  const [side, setSide] = useState<Side>(defaultSide);
  const [birthday, setBirthday] = useState<BirthdayValue>(emptyBirthday());
  const [livingStatus, setLivingStatus] = useState<LivingStatus>(null);
  const [notes, setNotes] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [city, setCity] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // ---- Connector picker (parent / "whose child / sibling / parent") ----
  // Used to attach the new member to the correct existing relative via the
  // existing parent_ids edge. Never creates new profiles or schema.

  const phase3RelationshipRuntimeProof = (stage: any, extra: Record<string, any> = {}) => {
    emitModernFamilyRuntimeProof({
      stage,
      relationship,
      customLabel,
      viewerMemberId,
      connectorId,
      secondConnectorId,
      candidateCount: Array.isArray(connectorCandidates) ? connectorCandidates.length : undefined,
      ...extra,
    });
  };

  const [connectorId, setConnectorId] = useState<string>('');
  /**
   * Optional second parent — enables dual-parent linking. Used when the
   * relationship stores the new member as a child of the connector(s)
   * (i.e. cousin / cousin-child / child). When set, the new row's
   * parent_ids becomes [connectorId, secondConnectorId] so siblings
   * sharing both parents are automatically recognised by the kinship
   * engine. Optional — leaving blank preserves the prior single-parent
   * behaviour exactly. Existing parent links are never overwritten.
   */
  const [secondConnectorId, setSecondConnectorId] = useState<string>('');

  // ---- DIAGNOSTIC PANEL (regression-repair, Nov 2026) ----
  // Visible error/result box rendered under the form after every save
  // attempt. Shows: create-person success/failure, edge success/failure,
  // raw DB error message, the relationship picked, the resolved anchor
  // id, the viewer member id, and the auth user id. Surfaces silent
  // failures the user previously couldn't see. Cleared automatically on
  // a fresh save attempt.
  const [lastDiag, setLastDiag] = useState<AddRelativeResult | null>(null);

  // Duplicate-check flow state. When true, we show the resolution panel
  // instead of immediately inserting.
  const [duplicateOverride, setDuplicateOverride] = useState<
    'create' | 'unsure' | null
  >(null);

  // ─── PHASE 7D — DUPLICATE PREVENTION GUARDRAIL ─────────────────────────
  // Pre-save scan state. Populated by handleSubmit when the
  // `duplicateGuardrail` flag is ON and the scanner returns at least one
  // match. The DuplicateConfirmModal renders when this is non-null.
  // Pure additive — when the flag is OFF this state stays null and the
  // existing add-member flow runs unchanged.
  const [guardrailMatches, setGuardrailMatches] = useState<DuplicateMatch[] | null>(null);
  const [guardrailDismissed, setGuardrailDismissed] = useState(false);


  // ---- DERIVED: literal `approval_status` to write on the next save ----
  // Surfaced in the visible diagnostic panel so testers can confirm the
  // gating without inspecting network traffic. Declared HERE (after
  // `duplicateOverride`) so we can reference it without hitting the
  // const TDZ. Used by `insertNew()`, the dev role-resolution diagnostic
  // panel, the submit-button label, the "pending approval" banner, and
  // the success-toast wording.
  const approvalStatusToWrite: 'approved' | 'pending' =
    duplicateOverride === 'unsure'
      ? 'pending'
      : canAddWithoutApproval
      ? 'approved'
      : 'pending';


  /**
   * Apply an extended-options entry to the form state — sets the
   * structural `relationship`, optional default `side` (only when the
   * caller hasn't explicitly forced one), and the custom display label.
   * This is the single source of truth for "what does picking an
   * extended option mean for the form?", reused both by the dropdown's
   * onValueChange handler AND the on-open initialization effect.
   */
  const applyExtendedKey = (key: string, opts?: { honorDefaultSide?: boolean }) => {
    setPreservedRelationshipWorkflow(null);

    const option = EXTENDED_RELATIONSHIP_OPTIONS.find((o) => o.value === key);
    if (!option) {
      // Unknown key — treat as free-form custom label only.
      setSelectedExtendedKey('unknown');
      setRelationship('unknown');
      setCustomLabel(key);
      return;
    }
    setSelectedExtendedKey(option.value);
    setRelationship(option.coreRelationship);
    // Only auto-apply the option's defaultSide when the caller asked us
    // to (used on initial open / when the user manually picks from the
    // dropdown). On a follow-up dropdown change we DON'T overwrite the
    // user's existing side selection — they might have intentionally
    // changed it.
    if (opts?.honorDefaultSide && option.defaultSide) {
      setSide(option.defaultSide);
    }
    // Pre-fill the custom label input with the entry's displayOverride
    // (if any) so the user sees exactly what label will be saved. Empty
    // string for core entries that don't need an override.
    setCustomLabel(option.displayOverride ?? '');
  };

  React.useEffect(() => {
    if (open) {
      setSide(defaultSide);
      setDuplicateOverride(null);
      // Resolve the initial extended key from BOTH `defaultRelationshipOverride`
      // (preferred — preserves the advanced label the user picked upstream)
      // AND `defaultRelationship` (fallback for core enum picks).
      const fromOverride = findExtendedOption(defaultRelationshipOverride);
      if (fromOverride) {
        applyExtendedKey(fromOverride.value, { honorDefaultSide: false });
        // Override the customLabel with the EXACT string the caller passed
        // (not the canonical displayOverride) so a free-form quick-pick
        // like "Custom: Godmother" shows the user's verbatim text.
        if (
          defaultRelationshipOverride &&
          defaultRelationshipOverride.trim() &&
          defaultRelationshipOverride.trim().toLowerCase() !==
            (fromOverride.label).toLowerCase()
        ) {
          setCustomLabel(defaultRelationshipOverride.trim());
        }
      } else if (defaultRelationship) {
        applyExtendedKey(defaultRelationship, { honorDefaultSide: false });
      } else if (defaultRelationshipOverride && defaultRelationshipOverride.trim()) {
        // Truly free-form — keep core relationship and use the typed label.
        setSelectedExtendedKey('unknown');
        setRelationship('unknown');
        setCustomLabel(defaultRelationshipOverride.trim());
      } else {
        applyExtendedKey('mother', { honorDefaultSide: false });
      }
      if (defaultName !== undefined) setName(defaultName);

      /*
       * Adopt the immutable card-originated workflow AFTER initial option
       * setup. applyExtendedKey intentionally clears stale workflow state.
       */
      setPreservedRelationshipWorkflow(
        defaultRelationshipWorkflow ?? null,
      );

      // CONTEXT-AWARE PARENT PRESELECT — when the user tapped the "+" on a
      // specific node, that node becomes the pre-selected parent for the
      // 'child' branch (rule: "the parent is ALWAYS the node the user
      // tapped from"). Falls back to '' so the global "Add to Mom's side"
      // button still surfaces an empty picker that the user must fill in.
      const workflowConnectorId =
        defaultRelationshipWorkflow
          ?.connectorMemberId ?? null;

      const workflowSecondaryConnectorId =
        defaultRelationshipWorkflow
          ?.secondaryConnectorMemberId ?? null;

      const workflowConnectorMember =
        workflowConnectorId
          ? members.find(
              member =>
                member.id === workflowConnectorId &&
                member.approvalStatus !== 'rejected',
            ) ?? null
          : null;

      if (
        defaultRelationshipWorkflow
          ?.inheritsBranch === true &&
        workflowConnectorMember
      ) {
        setSide(
          workflowConnectorMember.side,
        );
      }

      if (workflowConnectorId) {
        setConnectorId(workflowConnectorId);
      } else if (defaultParentId) {
        setConnectorId(defaultParentId);
      } else {
        setConnectorId('');
      }

      setSecondConnectorId(
        workflowSecondaryConnectorId ?? '',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    defaultSide,
    defaultRelationship,
    defaultName,
    defaultParentId,
    defaultRelationshipOverride,
    defaultRelationshipWorkflow,
  ]);


  const reset = () => {
    setName('');
    setRelationship('mother');
    setSelectedExtendedKey('mother');
    setCustomLabel('');
    setBirthday(emptyBirthday());
    setLivingStatus(null);
    setNotes('');
    setPhotoUrl('');
    setCity('');
    setDescription('');
    setDuplicateOverride(null);
    setConnectorId('');
    setSecondConnectorId('');
    setPreservedRelationshipWorkflow(null);
  };






  // ─── AUTO DUPLICATE-MATCHING (TEMPORARILY DISABLED) ───────────────────
  // Bug report (Nov 2026): the auto-duplicate matcher was hijacking the
  // manual add-relative flow — every newly-typed name was being matched
  // against an unrelated existing profile (Sharon Moultry-Henderson),
  // and the resulting "Connect" UI then surfaced a misleading
  // "Connected to Sharon Moultry-Henderson's existing profile" toast
  // even though no relationship edge ever made it to the database, no
  // person row was visibly created, and the user had not asked to
  // connect to an existing profile.
  //
  // Per the corrective spec: automatic existing-profile matching is
  // DISABLED for manual add-relative actions. The submit path is
  // always "create a new person" — never an auto-connect to anyone
  // already in the tree. The user can still invoke an EXPLICIT
  // "Connect to existing" workflow elsewhere (per-card "Set
  // relationship" action), but this modal will never auto-suggest a
  // match, never auto-show success for a connect, and never block
  // create on a soft duplicate. If the user types a name that exactly
  // matches a person already in the tree, we still let them save the
  // new row — duplicates can be merged later from the All Relatives
  // roster's Set-relationship menu, where the user is making an
  // informed, explicit pick (not racing against an autosuggest).
  //
  // Implementation notes:
  //   - `possibleDuplicates` is hard-pinned to [] so the duplicate
  //     panel never renders. We also short-circuit `findPossibleDuplicates`
  //     so it doesn't run for every keystroke.
  //   - `showDuplicatePanel` and `submitBlockedByDuplicate` follow.
  //   - `hasExactBlockingDuplicate` is kept-but-ignored for the same
  //     reason — if it ever becomes useful again, only the
  //     MANUAL_MATCH_DISABLED constant needs to flip.
  const MANUAL_MATCH_DISABLED = true as const;

  const possibleDuplicates = useMemo(() => {
    if (MANUAL_MATCH_DISABLED) return [];
    if (!name.trim() || name.trim().length < 2) return [];
    return findPossibleDuplicates(name, relationship, side, birthday.year ?? null).slice(0, 4);
  }, [name, relationship, side, birthday.year, findPossibleDuplicates]);

  // BLOCKING vs INFORMATIONAL split — kept for possible future re-enable,
  // but `MANUAL_MATCH_DISABLED` forces both flags off so the submit
  // button is never blocked by an auto-match and the duplicate panel
  // never renders.
  const normalizedTyped = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const hasExactBlockingDuplicate = useMemo(() => {
    if (MANUAL_MATCH_DISABLED) return false;
    if (!birthday.year) return false;
    if (!normalizedTyped) return false;
    return possibleDuplicates.some((d) => {
      const dn = (d.name ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      return dn === normalizedTyped && d.birthYear === birthday.year;
    });
  }, [possibleDuplicates, normalizedTyped, birthday.year]);

  const showDuplicatePanel =
    !MANUAL_MATCH_DISABLED &&
    possibleDuplicates.length > 0 &&
    duplicateOverride === null;
  const submitBlockedByDuplicate =
    !MANUAL_MATCH_DISABLED &&
    hasExactBlockingDuplicate &&
    duplicateOverride === null;

  // ---- Niece / Nephew detection ----------------------------------------
  // BUG FIX (Niece/Nephew was wrongly using cousin-child / extended-cousin
  // logic). A niece/nephew is the child of MY SIBLING (brother / sister /
  // half-sibling), NOT the child of my cousin. The structural relationship
  // is still stored as `cousin-child` (so the existing kinship engine and
  // tree placement keep working — children-of-siblings already resolve to
  // the niece/nephew label automatically via parent_ids walks). What
  // changes for THIS UI is the connector picker:
  //   • candidates pool → my siblings (relationship === 'sibling')
  //   • prompt          → "Which niece or nephew is this great-niece/great-nephew\'s parent?"
  //   • expected label  → "a sibling"
  //
  // Detection is loose-on-purpose:
  //   • the extended dropdown picked "Niece / Nephew" or
  //     "Great-Niece / Nephew" (selectedExtendedKey)
  //   • OR the visible custom label contains "niece" / "nephew"
  //     (covers the "+ Niece / Nephew" quick-add button passing
  //     a customLabel through `defaultRelationshipOverride`).
  // Plain "Cousin's Child" still uses the original cousin-only pool.
  const isNieceNephewMode = useMemo(() => {
    if (relationship !== 'cousin-child') return false;
    if (
      selectedExtendedKey === 'v_niece_nephew' ||
      selectedExtendedKey === 'v_great_niece_nephew'
    ) {
      return true;
    }
    const lc = (customLabel ?? '').toLowerCase();
    if (lc.includes('niece') || lc.includes('nephew')) return true;
    return false;
  }, [relationship, selectedExtendedKey, customLabel]);

  // ---- Connector candidates per relationship (STRICT mode) ----
  //
  // STRICT VALIDATION RULES — one connector type per relationship, no
  // multi-type pools. Mirrors SetRelationshipModal exactly so the
  // add-flow and the edit-flow agree on what's structurally valid.
  //
  //   cousin             → aunt-uncle ONLY
  //   cousin-child       → cousin    ONLY      (default)
  //                        sibling   ONLY      (when niece/nephew mode)
  //   aunt-uncle         → mother / father / step-parent / adopted-parent
  //                        (must share a parent with the viewer)
  //   grandparent        → mother / father / step-parent / adopted-parent
  //   great-grandparent  → grandparent ONLY
  //   child              → ANY live member  (CONTEXT-AWARE — the parent is
  //                        whichever node the user tapped "+" from; the
  //                        kinship engine derives child / grandchild /
  //                        great-grandchild from generational distance)
  //   sibling            → mother / father / step-parent / adopted-parent
  //                        (one or both — siblings inherit your parents)
  //
  // Anything outside this strict list is rejected at save time as well so
  // a stale UI selection can never silently write a wrong parent_id.
  // Dedup-by-id is applied at the end so a member can never appear twice
  // (defensive — the underlying members array shouldn't contain dupes,
  // but if a stale optimistic insert collides with a refresh row the
  // dropdown should still render each profile exactly once).
  const connectorCandidates = useMemo<GraphMember[]>(() => {
    const live = visibleMembers.filter((m) => m.approvalStatus !== 'rejected');

    let pool: GraphMember[] = [];
    switch (relationship) {
      case 'co-parent':
        emitGovernorLiveAction('coparent_child_selected', { source: 'AddGraphMemberModal' });
        pool = visibleMembers.filter((m) => {
          const rel = String(m?.relationship || '').toLowerCase();
          const parentIds = (m?.parentIds ?? m?.parent_ids ?? []).filter(Boolean);
          return rel === 'child' || (viewerMemberId ? parentIds.includes(viewerMemberId) : false);
        });
        break;
      case 'cousin':
        // STRICT: cousin's parent must be an aunt/uncle.
        pool = visibleMembers.filter((m) => m.relationship === 'aunt-uncle');
        break;
      case 'cousin-child':
        // SPLIT BY MODE:
        //   • Niece/Nephew → parent must be a SIBLING of mine.
        //   • Cousin's Child (default) → parent must be a cousin.
        if (isNieceNephewMode) {
          pool = visibleMembers.filter((m) => m.relationship === 'sibling');
        } else {
          pool = visibleMembers.filter((m) => m.relationship === 'cousin');
        }
        break;
      case 'child':
        // CONTEXT-AWARE: any existing live member can be the parent of a
        // child. The kinship engine reads generational distance from the
        // parent_ids walk and labels the new member as child / grandchild /
        // great-grandchild relative to YOU automatically. We surface the
        // viewer first so the YOU "+ → Child" quick path stays a single
        // tap; everyone else is sorted alphabetically. The previous
        // viewer+spouse-only restriction is removed (rule #2: "child uses
        // the selected node as parent" — no global gatekeeper).
        pool = live.slice().sort((a, b) => {
          const aIsViewer = viewerMemberId ? a.id === viewerMemberId : false;
          const bIsViewer = viewerMemberId ? b.id === viewerMemberId : false;
          if (aIsViewer && !bIsViewer) return -1;
          if (bIsViewer && !aIsViewer) return 1;
          return a.name.localeCompare(b.name);
        });
        break;
      case 'aunt-uncle':
        // STRICT-but-dual: aunt/uncle must connect via a SHARED-PARENT
        // relative — one of the viewer's parents (creates aunt/uncle)
        // OR one of the viewer's grandparents (creates great-aunt/uncle).
        // No siblings, cousins, children, or arbitrary relatives — those
        // don't represent a shared-parent connection. The kinship engine
        // derives the generation (aunt vs great-aunt) from the connector's
        // own parent_ids walk, so no extra wiring is needed here.
        pool = visibleMembers.filter(
          (m) =>
            m.relationship === 'mother' ||
            m.relationship === 'father' ||
            m.relationship === 'step-parent' ||
            m.relationship === 'adopted-parent' ||
            m.relationship === 'grandparent',
        );
        break;
      case 'sibling':
        pool = visibleMembers.filter(
          (m) =>
            m.relationship === 'mother' ||
            m.relationship === 'father' ||
            m.relationship === 'step-parent' ||
            m.relationship === 'adopted-parent',
        );
        break;
      case 'grandparent':
      case 'great-grandparent':
        /*
         * CC-TREE-LIVE-003G3A
         *
         * Ancestor candidate presentation consumes the same canonical
         * generation-anchor authority enforced by the lower writer.
         * Stored relationship labels never grant anchor eligibility.
         */
        pool =
          selectCanonicalTreeGenerationAnchorCandidates({
            members:
              visibleMembers,
            viewerMemberId:
              effectiveViewerId,
            relationship,
            optionKey:
              selectedExtendedKey,
            side,
          });
        break;
      case 'spouse': {
        // SPOUSE / IN-LAW PARTNER: the new member is the spouse of an
        // EXISTING relative — they get linked via spouse_id, NOT
        // parent_ids, so they appear BESIDE the partner on the same tier
        // and never inherit the partner's bloodline ancestry. Per the
        // in-law spec the candidate list is context-aware:
        //   • Son-in-law / Daughter-in-law → partner must be a child of
        //     yours (anyone who lists the viewer as a parent).
        //   • Brother-in-law / Sister-in-law → partner must be a sibling
        //     of yours (shares at least one parent_id with the viewer).
        //   • Plain "Spouse / Partner" → ANY live member can be the
        //     partner (covers viewer's own spouse, an aunt's spouse,
        //     a cousin's spouse, etc.). Candidates without an existing
        //     spouseId are surfaced first so users don't accidentally
        //     overwrite an already-linked couple.
        const label = (customLabel ?? '').toLowerCase();
        const isSonOrDaughterInLaw =
          label.includes('son-in-law') || label.includes('daughter-in-law');
        const isBroOrSisInLaw =
          label.includes('brother-in-law') || label.includes('sister-in-law');
        let spousePool = visibleMembers.filter((m) => {
          // Never offer the viewer themselves as their own spouse-link
          // unless the customLabel explicitly says "Spouse / Partner"
          // (someone editing their own marriage). The viewer is always
          // a valid candidate for any in-law variant.
          return true;
        });
        if (isSonOrDaughterInLaw && viewerMemberId) {
          spousePool = spousePool.filter((m) =>
            (m.parentIds ?? []).includes(viewerMemberId),
          );
        } else if (isBroOrSisInLaw && viewerMemberId) {
          const viewer = members.find((mm) => mm.id === viewerMemberId);
          const viewerParents = new Set(
            (viewer?.parentIds ?? []).filter(Boolean),
          );
          if (viewerParents.size > 0) {
            spousePool = spousePool.filter(
              (m) =>
                m.id !== viewerMemberId &&
                (m.parentIds ?? []).some((pid) => viewerParents.has(pid)),
            );
          }
        }
        // Sort: prefer relatives without an existing spouse so we don't
        // suggest overwriting an existing couple by accident.
        pool = spousePool.sort((a, b) => {
          const aHasSpouse = a.spouseId ? 1 : 0;
          const bHasSpouse = b.spouseId ? 1 : 0;
          if (aHasSpouse !== bHasSpouse) return aHasSpouse - bHasSpouse;
          return a.name.localeCompare(b.name);
        });
        break;
      }
      default:
        pool = [];
    }
    // Dedupe by id (defensive). Preserves first-occurrence order.
    const seen = new Set<string>();
    const out: GraphMember[] = [];
    for (const m of pool) {
      if (seen.has(m.id)) continue;
      seen.add(m.id);
      out.push(m);
    }
    return out;
  }, [
    members,
    relationship,
    viewerMemberId,
    effectiveViewerId,
    effectiveOwnerUserId,
    customLabel,
    isNieceNephewMode,
    selectedExtendedKey,
    side,
  ]);




  /**
   * Strict allow-list of connector relationship types per subject
   * relationship. Source of truth for save-time validation. Matches the
   * candidate filter above and SetRelationshipModal's allowedConnectorTypes.
   */
  const allowedConnectorTypes = (rel: Relationship): Relationship[] => {
    switch (rel) {
      case 'cousin':
        return ['aunt-uncle'];
      case 'cousin-child':
        return ['cousin'];
      case 'aunt-uncle':
        // Parent → aunt/uncle. Grandparent → great-aunt/great-uncle.
        // Both represent valid "shared-parent" connections; the kinship
        // engine derives the correct generation from the connector's
        // own parent_ids walk. No siblings/cousins/children allowed.
        return [
          'mother',
          'father',
          'step-parent',
          'adopted-parent',
          'grandparent',
        ];
      case 'grandparent':
      case 'sibling':
        return ['mother', 'father', 'step-parent', 'adopted-parent'];
      case 'great-grandparent':
        return selectedExtendedKey ===
          'v_great_great_grandparent'
          ? ['great-grandparent']
          : ['grandparent'];
      case 'child':
        // CONTEXT-AWARE: any live member can be a child's parent.
        // The selected node (passed via defaultParentId) IS the parent_id.
        // The kinship engine derives child / grandchild / great-grandchild
        // from generational distance — no hardcoded gatekeeper here.
        return [];
      default:
        return [];
    }
  };

  /** Returns true if `connector` is a valid parent for `rel`. */
  /** Returns true if `connector` is a valid parent for `rel`. */
  const isConnectorValid = (
    rel: Relationship,
    connector: GraphMember | null,
  ): boolean => {
    if (!connector) return false;
    if (rel === 'child' || rel === 'spouse') {
      // 'child' uses tapped node as parent — any live row is valid.
      // 'spouse' uses the picked partner as the spouse_id linker —
      // any live row is valid (in-law variants further filter the
      // candidate list to children/siblings of the viewer; the strict
      // structural check at save-time only requires a live row).
      return connector.approvalStatus !== 'rejected';
    }
    return allowedConnectorTypes(rel).includes(connector.relationship);
  };

  /** Friendly label describing the expected connector type, for errors. */
  const expectedConnectorLabel = (rel: Relationship): string => {
    switch (rel) {
      case 'cousin':
        return 'an aunt or uncle';
      case 'cousin-child':
        return 'a cousin';
      case 'aunt-uncle':
        return 'a parent (for aunt/uncle) or a grandparent (for great-aunt/uncle)';
      case 'grandparent':
      case 'sibling':
        return 'a parent (mother / father)';
      case 'great-grandparent':
        return selectedExtendedKey ===
          'v_great_great_grandparent'
          ? 'a great-grandparent'
          : 'a grandparent';
      case 'child':
        return 'an existing relative as the parent';
      case 'spouse':
        return 'an existing relative this person is married to / partnered with';
      default:
        return 'a valid relative';
    }
  };


  // ─── REGRESSION REPAIR (Nov 2026) ─────────────────────────────────────
  // The connector picker is now ALWAYS OPTIONAL. Per the current spec
  // ("The system must not block adding a relative just because a spouse
  // or second parent is missing"), `required: false` for every relationship
  // type. The picker is still SHOWN so users can connect when they want
  // to, but a missing pick never blocks save. Unconnected relatives land
  // in the "Needs connection" tier and can be linked later from the card.
  const connectorMeta = useMemo(() => {
    phase3RelationshipRuntimeProof('connector_candidates', { ok: true });
    switch (relationship) {
      case 'co-parent':
        return {
          show: true,
          required: true,
          prompt: "Who is this co-parent's shared child?",
        };
      case 'cousin':
        return {
          show: true,
          required: false,
          prompt: "Which aunt or uncle is this cousin's parent? (optional)",
        };
      case 'cousin-child':
        // SPLIT BY MODE — niece/nephew = sibling's child; cousin's child =
        // cousin's child. Different prompts so the user is asked the
        // structurally correct question for each.
        if (isNieceNephewMode) {
          return {
            show: true,
            required: false,
            prompt: "Which niece or nephew is this great-niece/great-nephew\'s parent? (optional)",
          };
        }
        return {
          show: true,
          required: false,
          prompt: "Which cousin is this child's parent? (optional)",
        };

      case 'child':
        return {
          show: true,
          required: false,
          prompt: "Who is this child's parent? (optional)",
        };

      case 'aunt-uncle':
        return {
          show: true,
          required: false,
          prompt:
            'Which parent or grandparent is this person related through? (optional — pick a parent for aunt/uncle, or a grandparent for great-aunt/great-uncle.)',
        };

      case 'grandparent':
        return {
          show: true,
          required: false,
          prompt: 'Which parent is this grandparent the parent of? (optional)',
        };
      case 'great-grandparent':
        return {
          show: true,
          required: false,
          prompt:
            selectedExtendedKey ===
            'v_great_great_grandparent'
              ? 'Which great-grandparent is this great-great-grandparent the parent of? (optional)'
              : 'Which grandparent is this great-grandparent the parent of? (optional)',
        };
      case 'sibling':
        return {
          show: true,
          required: false,
          prompt: 'Which parent(s) do you share? (optional)',
        };
      case 'spouse': {
        // SPOUSE / IN-LAW PARTNER: pick the existing relative this new
        // person is married to. Optional — saving without a pick still
        // creates the row; the user can link spouse_id later.
        const lc = (customLabel ?? '').toLowerCase();
        let prompt = 'Who is this person married to / partnered with? (optional)';
        if (lc.includes('son-in-law')) {
          prompt = 'Which of your children is the son-in-law married to? (optional)';
        } else if (lc.includes('daughter-in-law')) {
          prompt = 'Which of your children is the daughter-in-law married to? (optional)';
        } else if (lc.includes('brother-in-law')) {
          prompt = 'Which sibling of yours is the brother-in-law married to? (optional)';
        } else if (lc.includes('sister-in-law')) {
          prompt = 'Which sibling of yours is the sister-in-law married to? (optional)';
        }
        return { show: true, required: false, prompt };
      }
      default:
        return { show: false, required: false, prompt: '' };
    }
  }, [relationship, customLabel, isNieceNephewMode]);









  /**
   * Whether this relationship type stores the new member as a child of
   * the connector(s). Only these branches benefit from a 2nd parent
   * picker — for grandparent / great-grandparent / aunt-uncle the
   * parent edge is computed differently from the connector's own
   * parent_ids and the existing flow already handles that.
   *
   * SIBLING is included so the user can pick BOTH parents (mom + dad)
   * when adding a sibling. The new sibling's parent_ids becomes the
   * picked connector ids directly — this is what makes them
   * structurally a sibling of the viewer (sharing the same parents),
   * rather than the previous behaviour of inheriting the connector's
   * own parent_ids (which incorrectly produced an aunt/uncle-shaped
   * link in the kinship walk).
   */
  const isCoParentRelationship =
    relationship === 'co-parent';

  const supportsDualParent =
    relationship === 'cousin' ||
    relationship === 'cousin-child' ||
    relationship === 'child' ||
    relationship === 'sibling';

  // 2nd-parent dropdown re-uses the same candidate list, minus the 1st
  // pick, so we never write the same parent id twice and always reuse
  // existing profiles.
  const secondConnectorCandidates = useMemo(
    () => connectorCandidates.filter((c) => c.id !== connectorId),
    [connectorCandidates, connectorId],
  );

  /**
   * Render the right-hand "role" portion of a candidate's dropdown label.
   * BUG FIX — the logged-in viewer must show as "You" (never as
   * "Unknown / Possible Relation"). We also gracefully degrade any stale
   * 'unknown' label on a non-viewer to a softer "Relative" so the parent
   * picker never surfaces the literal "Unknown / Possible Relation"
   * label for someone the user is actively trying to connect through.
   */
  const candidateLabel = (c: GraphMember): string => {
    if (viewerMemberId && c.id === viewerMemberId) return 'You';
    if (c.relationship === 'self') return 'You';
    const stored = RELATIONSHIP_OPTIONS.find((o) => o.value === c.relationship)?.label;
    if (!stored || c.relationship === 'unknown') return 'Relative';
    return stored;
  };


  /**
   * Compute the parent_ids edge that the chosen connector(s) should
   * imply for the NEW member. Returns:
   *   - { mode: 'self', parents } → the new member's own parent_ids
   *     should be set to `parents` BEFORE/DURING insert (so the row
   *     hits the database with the correct edge in one shot — no race,
   *     no silent post-insert failure).
   *   - { mode: 'connector', connectorId, parents } → the CONNECTOR's
   *     parent_ids should be appended with the new member's id
   *     post-insert (reverse direction, used for grandparent /
   *     great-grandparent — the new member is the connector's parent).
   *   - { mode: 'none' } → no connector picked, or the connector
   *     produces no provable parent edge (e.g. aunt-uncle where the
   *     connector has no parent_ids of their own yet).
   *
   * CRITICAL: This is the SINGLE source of truth for "what parent_ids
   * does the user's connector pick imply?". The save flow uses this
   * to write parent_ids DIRECTLY on the insert row, never relying on
   * a separate post-insert update that could silently fail and leave
   * the new member unparented (which used to make them visually fall
   * back under the YOU card on the children tier — the exact bug
   * this change fixes).
   */
  const resolveConnectorEdge = ():
    | { mode: 'self'; parents: string[] }
    | { mode: 'connector'; connectorId: string; parents: string[] }
    | { mode: 'none' } => {
    if (!connectorId) return { mode: 'none' };
    const connector = members.find((m) => m.id === connectorId);
    if (!connector) return { mode: 'none' };
    const secondConnector =
      supportsDualParent && secondConnectorId && secondConnectorId !== connectorId
        ? members.find((m) => m.id === secondConnectorId) ?? null
        : null;
    if (
      relationship === 'cousin' ||
      relationship === 'cousin-child' ||
      relationship === 'child'
    ) {
      // "Whose child is this?" — connector(s) ARE the new member's parents.
      // Source-of-truth rule: the picked person becomes the parent_id, full
      // stop. No relationship-label-based override, no default-to-viewer
      // fallback. linkParents (called by addMember via insert payload) still
      // de-dupes and guards cycles defensively.
      const parents = [connector.id];
      if (secondConnector) parents.push(secondConnector.id);
      return { mode: 'self', parents };
    }
    if (relationship === 'sibling') {
      // Sibling shares the viewer's parents — the picked connector(s)
      // ARE the new sibling's parent_ids (NOT connector.parentIds).
      // This way, the kinship engine walks new_sibling → mom → grandma
      // and the viewer walks self → mom → grandma, sharing `mom` at
      // depth 1 on both sides → labelled as "Sibling". Optional 2nd
      // parent picker means a user who knows BOTH parents (mom + dad)
      // can dual-link in one step so siblings sharing both parents
      // get auto-grouped by the kinship engine.
      const parents = [connector.id];
      if (secondConnector) parents.push(secondConnector.id);
      return { mode: 'self', parents };
    }
    if (relationship === 'aunt-uncle') {
      // Aunt-uncle inherits the connector's OWN parents (so they sit
      // beside the connector on the tree as a sibling of a parent).
      // If the connector has no parent_ids yet there is nothing to
      // inherit — return 'none' rather than guessing the viewer.
      const parents = (connector.parentIds ?? []).filter(Boolean);
      if (parents.length === 0) return { mode: 'none' };
      return { mode: 'self', parents };
    }
    if (relationship === 'grandparent' || relationship === 'great-grandparent') {
      // Reverse direction: the new member is the connector's parent. The
      // connector's parent_ids gets a new entry post-insert. We preserve
      // any existing parent already linked on the connector so dual-
      // grandparent setups (grandma + grandpa) keep both edges intact.
      const existing = (connector.parentIds ?? []).filter(Boolean);
      return {
        mode: 'connector',
        connectorId: connector.id,
        parents: existing,
      };
    }
    return { mode: 'none' };
  };



  // ─── ensureViewerAnchor — production safety net ───────────────────────
  //
  // Some viewer-anchored relationships (mother / father / step / adopted /
  // sibling / child fallback) require a real person row for the
  // logged-in user before the universal edge writer can attach the new
  // relative. Invited paid users who haven't yet had their placeholder
  // claimed by the identity-binding pipeline land here with
  // `effectiveViewerId === null` — historically that aborted the save
  // with "Could not find your YOU profile" and the user saw the action
  // as silently blocked.
  //
  // This helper makes the add-relative path self-healing:
  //   1. If `effectiveViewerId` is already set, return it (fast path).
  //   2. Otherwise, look one more time for a user-scoped self-row
  //      (claimedByUserId === user.id  OR  ownerUserId === user.id
  //      AND side='self' AND placeholder relationship). If found and
  //      not yet claimed, claim it.
  //   3. Otherwise, create a fresh side='self' self-anchor row owned
  //      and claimed by the logged-in user, then return its id.
  //
  // The created row uses the same shape FamilyGraphSection's
  // `resolveSelfMember` would produce — relationship='unknown',
  // side='self', approval_status='approved', and immediately claimed
  // so the kinship engine, lineage filter, and pyramid layout all
  // treat it as YOU. Schema-safe, idempotent, additive only.
  //
  // NEVER touches: payments, subscriptions, access gating, auth,
  // pricing, uploads, invites, RLS policies, or relationship
  // assignment logic outside the viewer's own self-anchor row.
  const ensureViewerAnchor = async (): Promise<string | null> => {
    if (effectiveViewerId) return effectiveViewerId;
    if (!user?.id) return null;

    // Second-chance lookup (members may have updated since the memo).
    const live = visibleMembers.filter((m) => m.approvalStatus !== 'rejected');

    const claimedByMe = live.find((m) => m.claimedByUserId === user.id);
    if (claimedByMe) {
      return claimedByMe.id;
    }
    const myPlaceholder = live.find(
      (m) =>
        m.side === 'self' &&
        m.ownerUserId === user.id &&
        (m.relationship === 'unknown' || (m.relationship as string) === 'self'),
    );
    if (myPlaceholder) {
      if (myPlaceholder.claimedByUserId !== user.id) {
        try {
          await claimProfile(myPlaceholder.id);
        } catch {
          /* non-fatal — we'll still return the id */
        }
      }
      return myPlaceholder.id;
    }

    // Create a fresh self-anchor row owned & claimed by the logged-in
    // user, so the universal edge writer has a real DB row to append
    // parent_ids onto. Uses the same shape as the legacy auto-self
    // anchor (side='self', relationship='unknown', approved).
    try {
      const youDisplayName =
        auth?.memberName ||
        user?.email?.split('@')[0] ||
        'You';
      const created = emitGovernorLiveAction('member_add_attempt', { source: 'AddGraphMemberModal' });

      const parentIds = defaultParentId ? [defaultParentId].filter(Boolean) : [];

      const phase4Verdict = validateModernFamilyRelationship({
        stage: 'before_db_write',
        relationship,
        customLabel,
        viewerMemberId,
        connectorId,
        secondConnectorId,
        parentIds,
        candidateCount: Array.isArray(connectorCandidates) ? connectorCandidates.length : null,
      });

      phase3RelationshipRuntimeProof('phase4_governor_validator', {
        ok: phase4Verdict.ok,
        severity: phase4Verdict.severity,
        reason: phase4Verdict.reason,
        parentIds: phase4Verdict.parentIds,
      });

      if (!phase4Verdict.ok) {
        toast.error(phase4Verdict.reason || 'Governor blocked unsafe relationship write.');
        return;
      }

      phase3RelationshipRuntimeProof('db_write_started', {
        parentIds,
        ok: true,
        detail: 'About to write modern-family relationship row.',
      });

      await addMember({
        name: youDisplayName,
        relationship: 'unknown',
        side: 'self',
        approvalStatus: 'approved',
      });
      if (!created) return null;
      try {
        await claimProfile(created.id);
      } catch {
        /* non-fatal */
      }
      try {
        await refresh();
      } catch {
        /* non-fatal */
      }
      return created.id;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AddGraphMemberModal] ensureViewerAnchor failed', err);
      return null;
    }
  };


  // ─── insertNew — thin wrapper around addRelativeUniversal ─────────────
  // The single universal entry point lives in `src/lib/addRelativeUniversal.ts`.
  // This function ONLY:
  //   1. Resolves the anchor id from the form state (viewer-fallback for
  //      parent/step/adopted; picked connector for everything else).
  //   2. Calls addRelativeUniversal(deps, params).
  //   3. Stores the structured result in `lastDiag` so the visible
  //      diagnostic panel under the form renders person-insert
  //      success/failure, edge success/failure, the raw DB error, the
  //      relationship picked, the resolved anchor id, the viewer id,
  //      and the auth user id.
  //   4. On success, closes the modal. On failure, KEEPS the modal open
  //      so the user can read the diagnostic and retry without losing
  //      their typed input.
  //
  // The previous bespoke insertNew/edge-resolution logic — the bulk-add
  // wrapper, the resolveConnectorEdge helper, the manual reverse-edge
  // linkParents calls, the parent-of-YOU auto-link, the defensive retry,
  // the safety-timer pattern — is collapsed into the universal function.
  // No competing add-relative paths remain; every relationship type goes
  // through the same pipeline.
  const insertNew = async () => {
    setSubmitting(true);
    let timedOut = false;
    const safetyTimer = setTimeout(() => {
      timedOut = true;
      setSubmitting(false);
      toast.warning('Something paused — try again.');
    }, 10000);
    try {
      const bdayPayload = birthdayToPayload(birthday);

      // Runtime-safe governor/link proof.
      // Prevents parentIds ReferenceError during save.
      const parentIds: string[] = Array.from(
        new Set([connectorId, secondConnectorId].filter(Boolean) as string[])
      );

      // ─── VIEWER ANCHOR SAFETY NET (Dec 2026 production fix) ───────
      // For viewer-anchored relationships (parent / step / adopted /
      // sibling-via-fallback / child-via-fallback), guarantee a real
      // YOU row exists before the universal edge writer runs.
      // ensureViewerAnchor self-heals invited paid users who haven't
      // had their placeholder bound yet — the add never silently fails
      // on "no viewer" for those flows again.
      const needsViewerAnchor =
        relationship === 'mother' ||
        relationship === 'father' ||
        relationship === 'step-parent' ||
        relationship === 'adopted-parent' ||
        (relationship === 'sibling' && !connectorId) ||
        (relationship === 'child' && !connectorId);

      let viewerAnchorId: string | null = effectiveViewerId;
      if (needsViewerAnchor && !viewerAnchorId) {
        viewerAnchorId = await ensureViewerAnchor();
      }

      if (needsViewerAnchor && !viewerAnchorId) {
        // Self-heal failed — surface a clear blocking error rather
        // than silently writing the relative onto someone else's
        // anchor. The user can retry; the next render's
        // effectiveViewerId memo will pick up any newly-created row.
        const msg =
          'Could not find or create your "You" profile in this family. ' +
          'Try refreshing the page — if this persists, open your own card ' +
          'on the tree and tap "This is me" to bind your identity first.';
        toast.error(msg);
        setLastDiag({
          success: false,
          member: null,
          personError: msg,
          edgeError: null,
          databaseError: msg,
          relationship,
          anchorId: null,
          viewerId: null,
          userId: user?.id ?? null,
          parentIdsApplied: [],
          modeApplied: 'none',
          edgeVerified: false,
          timestamp: new Date().toISOString(),
        });
        return;
      }

      // Resolve the anchor id. For mother/father/step/adopted the anchor
      // IS the viewer (auto, self-healed above). For every other
      // relationship the anchor is whichever connector the user picked
      // (may be empty — universal function falls back to viewer for
      // child, and to floating insert for everything else without
      // throwing).
      let resolvedAnchor: string | null = null;
      if (
        relationship === 'mother' ||
        relationship === 'father' ||
        relationship === 'step-parent' ||
        relationship === 'adopted-parent'
      ) {
        resolvedAnchor = viewerAnchorId;
      } else if (relationship === 'child') {
        // Child's parent is the picked connector when set, otherwise viewer.
        resolvedAnchor = connectorId || viewerAnchorId;
      } else {
        resolvedAnchor = connectorId || null;
      }

      if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-console
        console.debug('[AddGraphMemberModal] universal save pre-flight', {
          targetName: name.trim(),
          relationship,
          side,
          resolvedAnchor,
          secondAnchor: secondConnectorId || null,
          viewerId: viewerAnchorId,
          userId: user?.id ?? null,
          isInSharedGraph,
        });
      }

      const workflowOption =
        findExtendedOption(selectedExtendedKey) ??
        findExtendedOption(customLabel.trim()) ??
        findExtendedOption(relationship);

      const creationPolicy =
        resolveCanonicalTreeRelationshipCreationPolicy(
          selectedExtendedKey,
        );

      if (creationPolicy.disposition !== 'CREATE') {
        toast.error(creationPolicy.reason);
        return;
      }

      const relationshipWorkflow =
        preservedRelationshipWorkflow ??
        (
          workflowOption
            ? buildRelationshipWorkflow(
                workflowOption,
                resolvedAnchor,
                secondConnectorId || null,
              )
            : null
        );

      const result = await addRelativeUniversal(
        {
          addMember,
          linkParents,
          linkSpouses,
          rollbackCreatedMember,
          refresh,
          members,
          viewerMemberId: viewerAnchorId,
          userId: user?.id ?? null,
        },
        {
          name: name.trim(),
          relationship,
          optionKey:
            selectedExtendedKey,
          anchorId: resolvedAnchor,
          secondAnchorId: secondConnectorId || null,
          side,
          relationshipOverride: customLabel.trim() || null,
          relationshipWorkflow,
          ...bdayPayload,
          livingStatus,
          notes: notes.trim() || null,
          city: city.trim() || null,
          description: description.trim() || null,
          photoUrl: photoUrl.trim() || null,
          approvalStatus: approvalStatusToWrite,

        },
      );

      // Always echo the structured result into the diagnostic panel so
      // the user can see EXACTLY what happened. Persists across the
      // modal until the next save attempt or until the modal closes.
      setLastDiag(result);

      if (!result.success) {
        // Person insert failed. Keep modal open so the user can read
        // the visible diagnostic + retry. Surface a toast for visibility.
        toast.error(result.personError || 'Could not save the person record.');
        return;
      }

      // Person row IS in the DB. Edge write may or may not have
      // succeeded — if it failed, surface a non-blocking toast (the row
      // is saved and visible in the roster regardless).
      if (result.edgeError) {
        toast.warning(
          `${name.trim()} was saved, but couldn't be linked: ${result.edgeError}`,
        );
      } else {
        phase3RelationshipRuntimeProof('db_write_finished', {
        parentIds,
        ok: true,
        detail: 'Modern-family relationship write returned without throwing.',
      });

      toast.success(
          canAddWithoutApproval && duplicateOverride !== 'unsure'
            ? `${name.trim()} added to your family tree`
            : `${name.trim()} submitted — pending approval`,
        );
      }


      // Audit (debug-only) — never throws.
      try {
        if (result.member) {
          debugAuditFamilyGraph(members.concat(result.member), {
            memberId: result.member.id,
            label: `add ${result.member.name} (${relationship})`,
          });
        }
      } catch {
        /* non-fatal */
      }

      if (timedOut) return;
      if (onAdded && result.member) onAdded(result.member);
      reset();
      
    emit('tree-member-save', {
      action: 'add-member',
      relationship,
      relationshipOverride,
      connectorRequired: connectorMeta?.required ?? false,
      connectorSelected: selectedConnectorId || null,
      name,
    });

    onOpenChange(false);
    } catch (err: any) {
      if (!timedOut) {
        // eslint-disable-next-line no-console
        console.error('[AddGraphMemberModal] save failed (outer catch)', err);
        const msg = err?.message || 'Could not add family member — please try again.';
        setLastDiag({
          success: false,
          member: null,
          personError: msg,
          edgeError: null,
          databaseError: msg,
          relationship,
          anchorId: connectorId || null,
          viewerId: effectiveViewerId,
          userId: user?.id ?? null,
          parentIdsApplied: [],
          modeApplied: 'none',
          edgeVerified: false,
          timestamp: new Date().toISOString(),
        });
        toast.error(msg);
      }
    } finally {
      clearTimeout(safetyTimer);
      setSubmitting(false);
    }
  };










  // "Connect to existing": link the existing member's relationship/side to the
  // user's input (in case the user thinks the duplicate was on the wrong side
  // or had stale info). We update the existing record rather than creating a
  // new one, then drop it into "pending" so an admin/2nd member can confirm
  // the merge.
  //
  // BUG FIX (Connect → existing father/mother doesn't appear in tree):
  // Previously this only updated the target's `relationship` / `side` /
  // descriptive fields. For mother/father/step-parent/adopted-parent
  // relationships that left the viewer's parent_ids untouched, so the
  // existing person STILL wasn't structurally linked as the viewer's
  // parent — they'd flip their label but stay floating in the tree.
  // We now ALSO call linkParents on the viewer to append the target's
  // id (preserving any other live parent already linked) so the connect
  // flow produces the same structural edge as inserting a brand-new
  // mother/father from scratch. parent_ids of every OTHER member stay
  // untouched — we only ever modify the viewer's own parent_ids.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    if (submitBlockedByDuplicate) {
      toast.info(
        'A profile with the same full name and birth year already exists. Pick Connect to merge, View to open it, or Create New Anyway to keep both.',
      );
      return;
    }

    const mandatoryDuplicateCheck =
      runDuplicatePreflight({
        source: 'add-graph-member',
        name: name.trim(),
        birthYear: birthday.year ?? null,
        birthDate:
          birthdayToPayload(birthday).birthDate ?? null,
        relationship,
        side,
        anchorId:
          relationship === 'mother' ||
          relationship === 'father' ||
          relationship === 'step-parent' ||
          relationship === 'adopted-parent'
            ? effectiveViewerId
            : relationship === 'child'
              ? connectorId || effectiveViewerId
              : connectorId || null,
        secondAnchorId:
          secondConnectorId || null,
        members,
      });

    if (
      mandatoryDuplicateCheck.action === 'block' &&
      !guardrailDismissed
    ) {
      setGuardrailMatches(
        mandatoryDuplicateCheck.matches,
      );
      return;
    }

    // Optional UI diagnostics remain feature-flag controlled.
    if (isDuplicateGuardrailEnabled() && !guardrailDismissed) {
      let resolvedAnchor: string | null = null;
      if (
        relationship === 'mother' ||
        relationship === 'father' ||
        relationship === 'step-parent' ||
        relationship === 'adopted-parent'
      ) {
        resolvedAnchor = effectiveViewerId;
      } else if (relationship === 'child') {
        resolvedAnchor = connectorId || effectiveViewerId;
      } else {
        resolvedAnchor = connectorId || null;
      }
      const matches = findDuplicateMatches({
        name: name.trim(),
        birthYear: birthday.year ?? null,
        birthDate: birthdayToPayload(birthday).birthDate ?? null,
        relationship,
        side,
        anchorId: resolvedAnchor,
        secondAnchorId: secondConnectorId || null,
        members,
      });
      if (matches.length > 0) {
        setGuardrailMatches(matches);
        return;
      }
      // No match — record a "no-match" event and proceed.
      recordDuplicateEvent({
        attemptedName: name.trim(),
        matchedName: null,
        matchedMemberId: null,
        confidence: null,
        action: 'no-match',
        source: 'add-graph-member',
        relationship,
      });
    }

    await insertNew();
  };

  // ─── PHASE 7D — DUPLICATE GUARDRAIL HANDLERS ───────────────────────────
  // Callers wired in the DuplicateConfirmModal below. Each one records an
  // event in the diagnostics ring buffer so owner / admin can review.
  const handleGuardrailUseExisting = async (existing: GraphMember) => {
    const top = guardrailMatches?.[0] ?? null;
    recordDuplicateEvent({
      attemptedName: name.trim(),
      matchedName: existing.name,
      matchedMemberId: existing.id,
      confidence: top?.confidence ?? null,
      action: 'used-existing',
      source: 'add-graph-member',
      relationship,
    });
    setGuardrailMatches(null);

    // Resolve anchor for the connect path. For viewer-parent relationships
    // we don't pass an anchorOverrideId (the universal helper falls back
    // to the viewer); for everything else we pass the picked connector
    // (parent / spouse / sibling pivot).
    const anchorOverride =
      relationship === 'mother' ||
      relationship === 'father' ||
      relationship === 'step-parent' ||
      relationship === 'adopted-parent'
        ? null
        : connectorId || null;

    const workflowOption =
      findExtendedOption(selectedExtendedKey) ??
      findExtendedOption(customLabel.trim()) ??
      findExtendedOption(relationship);

    const creationPolicy =
      resolveCanonicalTreeRelationshipCreationPolicy(
        selectedExtendedKey,
      );

    if (creationPolicy.disposition !== 'CREATE') {
      toast.error(creationPolicy.reason);
      return;
    }

    const relationshipWorkflow = workflowOption
      ? buildRelationshipWorkflow(
          workflowOption,
          anchorOverride,
          secondConnectorId || null,
        )
      : null;

    setSubmitting(true);
    try {
      const result = await connectExistingUniversal(
        {
          addMember,
          updateMember,
          linkParents,
          linkSpouses,
          refresh,
          members,
          viewerMemberId: effectiveViewerId,
          userId: user?.id ?? null,
        },
        {
          existingPersonId: existing.id,
          relationship,
          side,
          optionKey: selectedExtendedKey,
          relationshipWorkflow,
          relationshipOverride:
            customLabel.trim() || null,
          secondAnchorId: secondConnectorId || null,
          anchorOverrideId: anchorOverride,
        },
        connectExistingAncestryAtomic,
      );
      if (result.success) {
        toast.success(`Connected to ${existing.name}'s existing profile`);
        if (onAdded) onAdded(existing);
        reset();
        onOpenChange(false);
      } else {
        toast.error(
          result.edgeError ||
            `Could not connect ${existing.name} as your ${relationship}.`,
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not connect existing profile.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGuardrailCreateAnyway = async () => {
    const top = guardrailMatches?.[0] ?? null;
    recordDuplicateEvent({
      attemptedName: name.trim(),
      matchedName: top?.member.name ?? null,
      matchedMemberId: top?.member.id ?? null,
      confidence: top?.confidence ?? null,
      action: 'created-anyway',
      source: 'add-graph-member',
      relationship,
    });
    setGuardrailMatches(null);
    setGuardrailDismissed(true);
    await insertNew();
  };

  const handleGuardrailCancel = () => {
    const top = guardrailMatches?.[0] ?? null;
    recordDuplicateEvent({
      attemptedName: name.trim(),
      matchedName: top?.member.name ?? null,
      matchedMemberId: top?.member.id ?? null,
      confidence: top?.confidence ?? null,
      action: 'cancelled',
      source: 'add-graph-member',
      relationship,
    });
    setGuardrailMatches(null);
  };





  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl">
            <UserPlus className="w-5 h-5 text-fuchsia-600" />
            Add a family member
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="g-name">Name *</Label>
            <Input
              id="g-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDuplicateOverride(null);
              }}
              placeholder="Maria Rodriguez"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Side of family</Label>
              <Select value={side} onValueChange={(v) => setSide(v as Side)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mother">Mother's Side</SelectItem>
                  <SelectItem value="father">Father's Side</SelectItem>
                  <SelectItem value="both">Both / Shared</SelectItem>
                  <SelectItem value="self">My Side (you)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Relationship</Label>
              {/*
                SHARED EXTENDED-OPTIONS DROPDOWN — same source of truth as
                the quick-add menu (EXTENDED_RELATIONSHIP_OPTIONS in
                FamilyGraphContext). Includes every advanced label
                (Grandchild, Great-Grandchild, Great-Aunt/Uncle,
                Niece/Nephew, 2nd Cousin, Step-Mom/Dad, In-laws,
                Godparent, Family Friend, etc.) so whatever the user
                picked upstream is preserved verbatim and selectable
                here. Picking a virtual entry sets:
                  • structural `relationship` (core enum) — drives
                    parent_ids / connector / placement logic unchanged.
                  • visible custom label — written to
                    `relationship_override` so the kinship engine returns
                    that exact display string verbatim.
                  • optional default `side` — applied on user pick (not
                    on initial open, so the caller's defaultSide wins
                    when the upstream menu set it explicitly).
              */}
              <Select
                value={selectedExtendedKey}
                onValueChange={(v) => {
                  applyExtendedKey(v, { honorDefaultSide: true });
                  // Reset BOTH connectors when the structural relationship
                  // changes — candidate list is relationship-specific.
                  setConnectorId('');
                  setSecondConnectorId('');
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {/* Core relationships (most common — surfaced first) */}
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Common
                  </div>
                  {creatableRelationshipOptions.filter((o) => o.group === 'core').map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                  {/* Extended great/grand chain */}
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-t border-slate-100 mt-1">
                    Extended
                  </div>
                  {creatableRelationshipOptions.filter((o) => o.group === 'extended').map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                  {/* Step / adopted / half */}
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-t border-slate-100 mt-1">
                    Step / Adopted / Half
                  </div>
                  {creatableRelationshipOptions.filter((o) => o.group === 'step').map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                  {/* In-laws */}
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-t border-slate-100 mt-1">
                    In-laws
                  </div>
                  {creatableRelationshipOptions.filter((o) => o.group === 'in-law').map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                  {/* Other / chosen family */}
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-t border-slate-100 mt-1">
                    Other / Chosen Family
                  </div>
                  {creatableRelationshipOptions.filter((o) => o.group === 'other').map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Co-parent uses the single governed connector picker below. */}

{/* ---- Custom display label (shown for virtual / override-using
              entries OR whenever the user wants to override the default
              core label). Pre-filled with the selected option's
              displayOverride; the user can edit it before saving. The
              typed string is what `relationship_override` is set to so
              the kinship engine returns it verbatim — structural
              `relationship` enum + parent_ids logic stay unchanged. */}
          {(() => {
            const selectedOption = EXTENDED_RELATIONSHIP_OPTIONS.find(
              (o) => o.value === selectedExtendedKey,
            );
            const isVirtual =
              selectedOption?.displayOverride !== undefined &&
              !!selectedOption?.displayOverride;
            const isFreeForm =
              !!customLabel.trim() && !selectedOption;
            // Show the input when the entry HAS a displayOverride OR the
            // user typed a free-form label OR they explicitly selected
            // the 'unknown' bucket (which is the default catchall for
            // truly custom relationships like "Godmother", "Family Friend").
            const showCustomInput =
              isVirtual ||
              isFreeForm ||
              selectedExtendedKey === 'unknown';
            if (!showCustomInput) return null;
            
  const relationshipSpec = {
    connector: {
      show: connectorMeta?.show ?? false,
      required: connectorMeta?.required ?? false,
      prompt: connectorMeta?.prompt ?? 'Select the child this relationship connects through.',
    },
  };

return (
              <div className="rounded-xl border-2 border-purple-200 bg-purple-50/60 p-3 space-y-1.5">
                <Label htmlFor="g-custom-label" className="text-purple-900 text-xs font-bold">
                  Custom display label
                </Label>
                <Input
                  id="g-custom-label"
                  value={customLabel}
                  onChange={(e) => setCustomLabel(e.target.value)}
                  placeholder="Grandchild, Step-Mom, Godmother…"
                  className="bg-white"
                />
                <p className="text-[10px] text-purple-800/80 leading-snug">
                  This is what the card will display. Tree placement still
                  follows your family connections automatically — only the
                  visible label changes.
                </p>
              </div>
            );
          })()}


          {/* ---- Connection / parent picker ----
              Lets the user attach the new relative to the correct
              existing person via the existing parent_ids edge so the
              tree renders cousins under their aunt/uncle, nieces under
              the sibling, etc. Optional — if no connector is picked
              the relative still saves and stays in the roster as
              "needs connection". */}
          {((connectorMeta?.show ?? false) || relationship === 'co-parent') && (
            <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-3 space-y-2">
              <Label className="text-emerald-900">{(connectorMeta?.prompt ?? 'Select the child this relationship connects through.')}</Label>
              {/* Centralized friendly guidance from treeSafeguards — shown
                  below the prompt so the wording stays identical across
                  every surface that asks the same question. */}
              <p className="text-[11px] text-emerald-800/70 leading-snug">
                {relationshipGuidance(relationship)}
              </p>

              {connectorCandidates.length === 0 ? (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>
                    {(connectorMeta?.required ?? false) ? (
                      <>
                        We need {expectedConnectorLabel(relationship)} in your
                        tree before we can place this person. Add that relative
                        first, then come back here — they'll connect right away.
                      </>
                    ) : (
                      <>
                        No matching relative yet — you can still add this
                        person and connect them later from the roster's
                        "Set relationship" action.
                      </>
                    )}
                  </span>
                </div>
              ) : (
                <Select
                  value={connectorId || '__none__'}
                  onValueChange={(v) => {
                    const next = v === '__none__' ? '' : v;
                    setConnectorId(next);
                    // OPTIONAL second-parent auto-fill from picked parent's
                    // spouse_id. Pure UI HINT — the user can still change
                    // or clear the second parent freely. Never blocks the
                    // primary save flow. Only fills when the second slot
                    // is currently empty AND the chosen parent's spouse
                    // is in the available candidate pool.
                    if (!next) {
                      setSecondConnectorId('');
                    } else if (supportsDualParent && !secondConnectorId) {
                      const picked = members.find((m) => m.id === next);
                      const spouseId = picked?.spouseId ?? null;
                      if (spouseId && spouseId !== next) {
                        const spouseInPool = secondConnectorCandidates.some(
                          (c) => c.id === spouseId,
                        );
                        if (spouseInPool) setSecondConnectorId(spouseId);
                      }
                    }
                  }}
                >


                  <SelectTrigger><SelectValue placeholder="Pick a person…" /></SelectTrigger>
                  <SelectContent>
                    {/*
                      AUTO-CONNECT-ON-CREATE rule: when the picker is
                      REQUIRED, deliberately DON'T surface the legacy
                      "Don't connect yet" escape hatch — every newly-
                      added relative MUST land connected so they never
                      appear in the bottom "Needs connection" row by
                      default. The "Set relationship" button on each
                      card is still the path for fixing missed links
                      on existing rows. Only when the picker is
                      OPTIONAL (e.g. 'child' with a tapped node already
                      pre-selected) do we keep the explicit "Don't
                      connect yet" option.
                    */}
                    {!(connectorMeta?.required ?? false) && (
                      <SelectItem value="__none__">
                        — Don't connect yet (mark as Needs connection) —
                      </SelectItem>
                    )}
                    {connectorCandidates.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} · {candidateLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}


              {/* ------- Optional 2nd parent picker (dual-parent support) -------
                  Only shown for relationships where the new member is
                  stored as a child of the connector(s). Lets users link
                  BOTH parents in one step — siblings sharing both parents
                  are then auto-recognised by the kinship engine without
                  any extra wiring. */}
              {supportsDualParent && connectorId && connectorCandidates.length > 0 && (
                <div className="pt-1">
                  <Label className="text-emerald-900 text-xs">
                    Second parent (optional)
                  </Label>
                  {secondConnectorCandidates.length === 0 ? (
                    <p className="text-[11px] text-emerald-800/80 mt-1">
                      Add another possible parent first, then come back to
                      link both. One parent is fine for now.
                    </p>
                  ) : (
                    <Select
                      value={secondConnectorId || '__none__'}
                      onValueChange={(v) =>
                        setSecondConnectorId(v === '__none__' ? '' : v)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Pick the second parent…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">
                          — None / unknown —
                        </SelectItem>
                        {secondConnectorCandidates.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name} · {candidateLabel(c)}
                          </SelectItem>
                        ))}

                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}

              {connectorId ? (
                <p className="text-[11px] text-emerald-800/80">
                  Relationship to you will be calculated automatically from
                  this parent link — placement on the tree follows parent_ids,
                  not the relationship label.
                </p>
              ) : (
                <p className="text-[11px] text-emerald-800/80">
                  Picking someone here only links the existing relationship — it
                  never creates a duplicate profile.
                  {supportsDualParent && (
                    <>
                      {' '}You can link both parents — siblings sharing them
                      are automatically grouped together.
                    </>
                  )}
                </p>
              )}

            </div>
          )}


          <BirthdayInput value={birthday} onChange={setBirthday} />


          <div>
            <Label>Status (optional)</Label>
            <Select
              value={livingStatus ?? 'unknown'}
              onValueChange={(v) =>
                setLivingStatus(v === 'unknown' ? null : (v as LivingStatus))
              }
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unknown">Not specified</SelectItem>
                <SelectItem value="living">Living</SelectItem>
                <SelectItem value="deceased">Deceased</SelectItem>
              </SelectContent>
            </Select>
          </div>


          <div>
            <Label htmlFor="g-city">City / Location (optional)</Label>
            <Input
              id="g-city"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Buenos Aires"
            />
          </div>

          {/* Photo upload — drag & drop or file picker. Falls back to URL. */}
          <div>
            <Label>Profile photo (optional)</Label>
            <div className="flex items-start gap-4 mt-1">
              <PhotoUpload
                folder="graph-avatars"
                userId={user?.id ?? null}
                currentUrl={photoUrl || null}
                onUploaded={({ url }) => setPhotoUrl(url)}
                onRemove={() => setPhotoUrl('')}
                variant="avatar"
                hint="JPG/PNG/WEBP · max 5 MB"
              />
              <div className="flex-1 min-w-0">
                <Label htmlFor="g-photo" className="text-xs text-slate-500">
                  …or paste a URL
                </Label>
                <Input
                  id="g-photo"
                  value={photoUrl}
                  onChange={(e) => setPhotoUrl(e.target.value)}
                  placeholder="https://…"
                  className="mt-1"
                />
              </div>
            </div>
          </div>


          <div>
            <Label htmlFor="g-desc">Short description (optional)</Label>
            <Input
              id="g-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Loves baking, lived in Lima for 30 years"
            />
          </div>

          <div>
            <Label htmlFor="g-notes">Notes / memories (optional)</Label>
            <Textarea
              id="g-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Stories, memories, or anything to remember..."
            />
          </div>


          {!canAddWithoutApproval && !showDuplicatePanel && (
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
              New members are marked <strong>Pending</strong> until the family
              owner, an admin, or 2 family members confirm them.
            </div>
          )}

          {/* ─── DEV ROLE-RESOLUTION DIAGNOSTIC PANEL ───────────────────
              VISIBILITY GATE — both conditions required:
                1. running on a deploypad.app dev/staging host
                   (DEV_BYPASS_TEST_MODE === true)
                2. the authenticated user is staff (owner / admin role)

              On every production host (kinscircleapp.com /
              www.kinscircleapp.com / any non-deploypad domain) this
              evaluates to false → the entire amber diagnostic panel is
              omitted. Consumers never see raw auth uids, viewer member
              ids, role labels, or "approval_status to write" details. */}
          {DEV_BYPASS_TEST_MODE && isStaff && (
            <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 space-y-1 text-[11px] font-mono text-amber-900">
              <div className="font-sans font-semibold text-xs flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                Dev role-resolution (deploypad.app · staff only)
              </div>
              <div>auth.uid: <strong>{user?.id ?? 'none'}</strong></div>
              <div>viewerMemberId: <strong>{viewerMemberId ?? 'none'}</strong></div>
              <div>role: <strong>{role}</strong></div>
              <div>isOwner: <strong>{String(isOwner)}</strong> · isStaff: <strong>{String(isStaff)}</strong></div>
              <div>
                canAddWithoutApproval: <strong>{String(canAddWithoutApproval)}</strong>
              </div>
              <div>
                approval_status to write: <strong>{approvalStatusToWrite}</strong>
              </div>
            </div>
          )}



          {/* ─── VISIBLE DIAGNOSTIC PANEL (DEV / STAFF ONLY) ─────────────
              Renders the structured result of the most recent save attempt
              so silent failures become visible: person insert success/
              failure, edge success/failure, raw DB error message, the
              relationship picked, the resolved anchor id, the viewer
              member id, and the auth user id. Cleared when the user
              starts a new save attempt; persists across the modal so
              they can read it after a failed save.

              VISIBILITY GATE — both conditions required:
                1. running on a deploypad.app dev/staging host
                   (DEV_BYPASS_TEST_MODE === true)
                2. the authenticated user is staff (owner / admin role)

              Production users never see raw ids, DB error strings, or
              "Save diagnostic" panels. The structured `lastDiag` is still
              populated internally so console diagnostics + future internal
              repair pipelines keep working — only the visible UI is gated. */}
          {lastDiag && DEV_BYPASS_TEST_MODE && isStaff && (

            <div
              className={`rounded-xl border-2 p-3 space-y-1.5 text-[11px] font-mono ${
                lastDiag.success && !lastDiag.edgeError
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : lastDiag.success
                  ? 'bg-amber-50 border-amber-300 text-amber-900'
                  : 'bg-rose-50 border-rose-300 text-rose-900'
              }`}
            >
              <div className="flex items-center gap-1.5 font-sans font-semibold text-xs">
                {lastDiag.success && !lastDiag.edgeError ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Save diagnostic — success
                  </>
                ) : lastDiag.success ? (
                  <>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Save diagnostic — saved with edge warning
                  </>
                ) : (
                  <>
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Save diagnostic — failed
                  </>
                )}
              </div>
              <div>
                Person insert:{' '}
                <strong>
                  {lastDiag.success ? 'OK' : 'FAILED'}
                  {lastDiag.member?.id ? ` (id: ${lastDiag.member.id.slice(0, 8)}…)` : ''}
                </strong>
              </div>
              <div>
                Edge insert:{' '}
                <strong>
                  {lastDiag.success
                    ? lastDiag.edgeError
                      ? 'FAILED'
                      : lastDiag.parentIdsApplied.length > 0
                      ? `OK (parent_ids: ${lastDiag.parentIdsApplied.length})`
                      : 'OK (no edge — floating)'
                    : 'skipped'}
                </strong>
              </div>
              {lastDiag.databaseError && (
                <div className="break-words">
                  DB error: <strong>{lastDiag.databaseError}</strong>
                </div>
              )}
              <div>
                Relationship: <strong>{lastDiag.relationship}</strong>
              </div>
              <div>
                Selected anchor:{' '}
                <strong>{lastDiag.anchorId ? lastDiag.anchorId.slice(0, 8) + '…' : 'none'}</strong>
              </div>
              <div>
                Viewer member id:{' '}
                <strong>{lastDiag.viewerId ? lastDiag.viewerId.slice(0, 8) + '…' : 'none'}</strong>
              </div>
              <div>
                Logged-in user id:{' '}
                <strong>{lastDiag.userId ? lastDiag.userId.slice(0, 8) + '…' : 'none'}</strong>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || submitBlockedByDuplicate}
              className="bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {canAddWithoutApproval && duplicateOverride !== 'unsure'
                ? 'Save member'
                : 'Submit for approval'}
            </Button>
          </div>

        </form>
      </DialogContent>
      <DuplicateConfirmModal
        open={!!guardrailMatches && guardrailMatches.length > 0}
        attemptedName={name.trim()}
        matches={guardrailMatches ?? []}
        isStaff={isStaff || canAddWithoutApproval}
        onUseExisting={handleGuardrailUseExisting}
        onCreateAnyway={handleGuardrailCreateAnyway}
        onCancel={handleGuardrailCancel}
      />
    </Dialog>
  );
};

export default AddGraphMemberModal;
